import React, {createContext, useContext, useEffect, useState} from 'react';
import tagModel from '../model/tag';

const TagContext = createContext(null);

const TTL = 300000; // 5min in ms

const TagProvider = ({children}) => {
  const [tags, setTags] = useState([]);
  const [lastFetched, setLastFetched] = useState(null);

  const getTags = async (forceRefresh = false) => {
    const ttlPassed = lastFetched === null || (Date.now() - lastFetched) > TTL;

    // If not force refreshing and TTL has not passed, return what we have.
    if (!forceRefresh && !ttlPassed) {
      return tags;
    }

    return await fetchTags();
  };

  const fetchTags = async () => {
    const tags = await tagModel.getAll();
    const sortedTags = tags.sort((first, second) => first.name.localeCompare(second.name));

    setTags(sortedTags);
    setLastFetched(Date.now());

    return sortedTags;
  };

  useEffect(() => {
    fetchTags();
  }, []);

  return (
    <TagContext.Provider value={{
      getTags: getTags,
      refreshTags: fetchTags,
      lastFetched: lastFetched
    }}>
      {children}
    </TagContext.Provider>
  );
};

const useTags = () => {
  const context = useContext(TagContext);
  if (context === null) {
    throw new Error('Tag context not found. Component must be nested below the TagProvider');
  }

  return context;
};

function hocWrapper() {
  function hoc(InputComponent) {
    function WrapperComponent(props) {
      const tags = useTags();

      return <InputComponent tags={tags} {...props} />;
    }

    return WrapperComponent;
  }

  return hoc;
}

const useTagsHoC = hocWrapper();

export {useTags, TagProvider, useTagsHoC};
